import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import type { LeadWithDraft, MessageTemplate } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import {
  getLeadsWithDrafts,
  updateLeadStatus,
  updateProfile,
  getProfile,
  generateEmail,
  saveEmailDraft,
  updateEmailDraft,
} from '@traction/api-client';
import { useToast } from '@/components/Toast';
import {
  Coffee,
  Send,
  Edit3,
  X,
  FileText,
  Plus,
  Trash2,
  Save,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Mail,
  PenLine,
  RefreshCw,
} from 'lucide-react';

// Default templates for first-time users
const DEFAULT_TEMPLATES: Omit<MessageTemplate, 'id' | 'user_id' | 'created_at' | 'updated_at'>[] = [
  {
    name: 'Simple Intro',
    subject: '{{my_title}}',
    body: `Hi {{first_name}}, I'm a {{my_title}}{{city_phrase}}. MVPs and senior web engineering. Open to a quick call if that's useful for {{company_name}}?{{phone_phrase}}`,
    tone: 'warm',
    is_default: true,
  },
];

/**
 * Remove one or more trailing copies of the profile signature from the draft body.
 * Older flows appended email_signature in the DB and Morning Brief also rendered it below the message,
 * which looked like a double block; some rows may still have duplicate trailing signatures.
 */
function trimRepeatedTrailingSignature(body: string, sig: string): string {
  if (!body?.trim() || !sig?.trim()) return body;
  const t = sig.trim().replace(/\r\n/g, '\n');
  if (t.length < 3) return body;
  let result = body.replace(/\r\n/g, '\n').replace(/\s+$/, '');
  const suffix = `\n\n${t}`;
  // Only strip when at least two identical trailing blocks (true duplicates), not a single signature.
  while (result.endsWith(suffix + suffix)) {
    result = result.slice(0, -suffix.length).replace(/\s+$/, '');
  }
  return result.trimEnd() ? result.trimEnd() : body;
}

export function MorningBriefPage() {
  const { user, profile: authProfile } = useAuth();
  const toast = useToast();

  // Leads state
  const [leads, setLeads] = useState<LeadWithDraft[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [skippingLead, setSkippingLead] = useState<string | null>(null);
  const [sendingLead, setSendingLead] = useState<string | null>(null);

  // Signature state
  const [signature, setSignature] = useState('');
  const [signatureExpanded, setSignatureExpanded] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [templatesExpanded, setTemplatesExpanded] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [newTemplate, setNewTemplate] = useState<Partial<MessageTemplate> | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Selected lead for detailed view
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState(false);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  // Load leads and profile
  useEffect(() => {
    async function load() {
      if (!user) return;
      try {
        const [allLeads, profileData] = await Promise.all([
          getLeadsWithDrafts(user.uid),
          getProfile(user.uid),
        ]);
        setLeads(allLeads);

        if (profileData?.email_signature) {
          setSignature(profileData.email_signature);
        }

        // Load templates from localStorage for now (can be migrated to DB later)
        const savedTemplates = localStorage.getItem(`templates_${user.uid}`);
        if (savedTemplates) {
          setTemplates(JSON.parse(savedTemplates));
        } else {
          // Initialize with default templates
          const initialTemplates = DEFAULT_TEMPLATES.map((t, i) => ({
            ...t,
            id: `template_${i}`,
            user_id: user.uid,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }));
          setTemplates(initialTemplates);
          localStorage.setItem(`templates_${user.uid}`, JSON.stringify(initialTemplates));
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoadingLeads(false);
      }
    }
    load();
  }, [user]);

  // Ready-to-send leads (must have real email, not apollo placeholder)
  const readyToSend = useMemo(() => {
    return leads.filter((l) => l.status === 'pending' && l.email_draft && l.email && !l.email.includes('@apollo.local'));
  }, [leads]);

  // Leads that need email enrichment (have draft but no real email)
  const needsEnrichment = useMemo(() => {
    return leads.filter((l) => l.status === 'pending' && l.email_draft && (!l.email || l.email.includes('@apollo.local')));
  }, [leads]);

  // Selected lead
  const selectedLead = useMemo(() => {
    return leads.find((l) => l.id === selectedLeadId) || null;
  }, [leads, selectedLeadId]);

  // Update draft state when selected lead changes
  useEffect(() => {
    if (selectedLead?.email_draft) {
      setDraftSubject(selectedLead.email_draft.subject);
      setDraftBody(selectedLead.email_draft.body);
    }
  }, [selectedLead]);

  // Handle skip
  async function handleSkipLead(leadId: string) {
    setSkippingLead(leadId);
    try {
      await updateLeadStatus(leadId, 'skipped');
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: 'skipped' } : l)));
      if (selectedLeadId === leadId) {
        setSelectedLeadId(null);
      }
      toast.success('Lead skipped');
    } catch (error) {
      console.error('Failed to skip lead:', error);
      toast.error('Failed to skip lead');
    } finally {
      setSkippingLead(null);
    }
  }

  // Handle send (opens Gmail)
  async function handleSendLead(lead: LeadWithDraft) {
    if (!lead.email || !lead.email_draft) return;

    setSendingLead(lead.id);
    try {
      // Use draft body only; strip duplicate trailing profile signatures from legacy data.
      const body = trimRepeatedTrailingSignature(lead.email_draft.body, signature);
      const subject = encodeURIComponent(lead.email_draft.subject || 'Quick intro');
      const encodedBody = encodeURIComponent(body);
      const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(lead.email)}&su=${subject}&body=${encodedBody}`;

      // Open Gmail in new tab
      window.open(gmailUrl, '_blank');

      // Mark as contacted
      await updateLeadStatus(lead.id, 'contacted');
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status: 'contacted' } : l)));

      toast.success('Email opened in Gmail');
    } catch (error) {
      console.error('Failed to send:', error);
      toast.error('Failed to open email');
    } finally {
      setSendingLead(null);
    }
  }

  // Save signature
  async function handleSaveSignature() {
    if (!user) return;
    setSavingSignature(true);
    try {
      await updateProfile(user.uid, { email_signature: signature });
      toast.success('Signature saved');
    } catch (error) {
      console.error('Failed to save signature:', error);
      toast.error('Failed to save signature');
    } finally {
      setSavingSignature(false);
    }
  }

  // Save template
  function handleSaveTemplate(template: MessageTemplate) {
    if (!user) return;
    setSavingTemplate(true);
    try {
      const updatedTemplates = templates.map((t) =>
        t.id === template.id ? { ...template, updated_at: new Date().toISOString() } : t
      );
      setTemplates(updatedTemplates);
      localStorage.setItem(`templates_${user.uid}`, JSON.stringify(updatedTemplates));
      setEditingTemplate(null);
      toast.success('Template saved');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  }

  // Add new template
  function handleAddTemplate() {
    if (!user || !newTemplate?.name || !newTemplate?.body) return;
    setSavingTemplate(true);
    try {
      const template: MessageTemplate = {
        id: `template_${Date.now()}`,
        user_id: user.uid,
        name: newTemplate.name,
        subject: newTemplate.subject || '',
        body: newTemplate.body,
        tone: newTemplate.tone || 'warm',
        is_default: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      const updatedTemplates = [...templates, template];
      setTemplates(updatedTemplates);
      localStorage.setItem(`templates_${user.uid}`, JSON.stringify(updatedTemplates));
      setNewTemplate(null);
      toast.success('Template created');
    } catch (error) {
      console.error('Failed to create template:', error);
      toast.error('Failed to create template');
    } finally {
      setSavingTemplate(false);
    }
  }

  // Delete template
  function handleDeleteTemplate(templateId: string) {
    if (!user) return;
    const updatedTemplates = templates.filter((t) => t.id !== templateId);
    setTemplates(updatedTemplates);
    localStorage.setItem(`templates_${user.uid}`, JSON.stringify(updatedTemplates));
    toast.success('Template deleted');
  }

  // Apply template to selected lead
  async function handleApplyTemplate(template: MessageTemplate, lead: LeadWithDraft) {
    if (!user) return;

    // Replace placeholders
    let subject = template.subject;
    let body = template.body;

    const cityRaw = (authProfile?.city || authProfile?.location || '').trim();
    const cityPhrase = cityRaw ? ` in ${cityRaw.split(',')[0].trim()}` : '';
    const phoneRaw = (authProfile?.phone || '').trim();
    const phonePhrase = phoneRaw ? ` ${phoneRaw} (text is fine too).` : '';

    const replacements: Record<string, string> = {
      '{{first_name}}': lead.first_name,
      '{{last_name}}': lead.last_name,
      '{{company_name}}': lead.company_name || 'your company',
      '{{title}}': lead.title || '',
      '{{signal_details}}': lead.signal_details || 'making great progress',
      '{{my_name}}': authProfile?.first_name || authProfile?.full_name?.split(' ')[0] || '',
      '{{my_title}}': authProfile?.current_title || 'CTO',
      '{{my_city}}': authProfile?.city || '',
      '{{my_phone}}': authProfile?.phone || '',
      '{{city_phrase}}': cityPhrase,
      '{{phone_phrase}}': phonePhrase,
      '{{value_prop}}': authProfile?.bio || 'achieve your goals faster',
    };

    for (const [key, value] of Object.entries(replacements)) {
      subject = subject.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
      body = body.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
    }

    setDraftSubject(subject);
    setDraftBody(body);
    setEditingDraft(true);
  }

  // Save draft edits
  async function handleSaveDraft() {
    if (!user || !selectedLead) return;

    try {
      if (selectedLead.email_draft?.id) {
        await updateEmailDraft(selectedLead.email_draft.id, {
          subject: draftSubject,
          body: draftBody,
        });
      } else {
        await saveEmailDraft(user.uid, selectedLead.id, {
          subject: draftSubject,
          body: draftBody,
          tone: 'warm',
        });
      }

      // Update local state
      setLeads((prev) =>
        prev.map((l) =>
          l.id === selectedLead.id
            ? { ...l, email_draft: { ...l.email_draft, subject: draftSubject, body: draftBody } as any }
            : l
        )
      );

      setEditingDraft(false);
      toast.success('Draft saved');
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft');
    }
  }

  // Regenerate email for selected lead
  async function handleRegenerate() {
    if (!user || !selectedLead) return;

    setRegenerating(true);
    try {
      const result = await generateEmail(user.uid, selectedLead.id, 'friendly');
      if (result?.success && result.email) {
        setDraftSubject(result.email.subject);
        setDraftBody(result.email.body);
        setEditingDraft(true);
        toast.success('Email regenerated');
      }
    } catch (error) {
      console.error('Failed to regenerate:', error);
      toast.error('Failed to regenerate email');
    } finally {
      setRegenerating(false);
    }
  }

  if (loadingLeads) {
    return (
      <div className="min-h-screen p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cacao-gold to-amber-500 shadow-lg shadow-cacao-gold/20">
            <Coffee className="h-6 w-6 text-cacao-dark" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-white">Morning Brief</h1>
            <p className="text-cacao-muted">
              {readyToSend.length > 0 ? (
                <span className="text-green-400">{readyToSend.length} ready to send</span>
              ) : (
                <span>No emails ready</span>
              )}
              {needsEnrichment.length > 0 && (
                <span className="text-amber-400 ml-2">
                  · {needsEnrichment.length} need{needsEnrichment.length === 1 ? 's' : ''} enrichment
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Ready to Send */}
        <div className="lg:col-span-2 space-y-4">
          {readyToSend.length === 0 ? (
            <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-8 text-center">
              {needsEnrichment.length > 0 ? (
                <>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10">
                    <Mail className="h-8 w-8 text-amber-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    {needsEnrichment.length} lead{needsEnrichment.length !== 1 ? 's' : ''} need{needsEnrichment.length === 1 ? 's' : ''} email
                  </h3>
                  <p className="mt-2 text-sm text-cacao-muted max-w-sm mx-auto">
                    These leads have draft emails ready, but you need to <span className="text-amber-400 font-medium">reveal their email address</span> first using the Enrich button.
                  </p>
                  <Link
                    to={ROUTES.LEADS}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-cacao-dark hover:bg-amber-400 transition-colors"
                  >
                    <Sparkles className="h-4 w-4" />
                    Enrich Leads Now
                  </Link>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-cacao-gold/10">
                    <Mail className="h-8 w-8 text-cacao-gold" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">No emails ready</h3>
                  <p className="mt-2 text-sm text-cacao-muted">
                    Generate emails for your pending leads to see them here.
                  </p>
                  <Link
                    to={ROUTES.LEADS}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-cacao-gold px-4 py-2 text-sm font-semibold text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                  >
                    <Sparkles className="h-4 w-4" />
                    Go to Leads
                  </Link>
                </>
              )}
            </div>
          ) : (
            readyToSend.map((lead) => (
              <div
                key={lead.id}
                className={`rounded-xl border bg-cacao-dark-secondary transition-all ${
                  selectedLeadId === lead.id
                    ? 'border-cacao-gold'
                    : 'border-cacao-dark-tertiary hover:border-cacao-gold/30'
                }`}
              >
                {/* Lead Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => setSelectedLeadId(selectedLeadId === lead.id ? null : lead.id)}
                >
                  <div className="flex items-center gap-3">
                    {lead.photo_url ? (
                      <img
                        src={lead.photo_url}
                        alt=""
                        className="h-10 w-10 rounded-full object-cover border border-cacao-dark-tertiary"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cacao-gold/20 text-sm font-semibold text-cacao-gold">
                        {lead.first_name[0]}{lead.last_name[0]}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-white">{lead.first_name} {lead.last_name}</p>
                      <p className="text-xs text-cacao-muted">{lead.title} at {lead.company_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-400">
                      Ready
                    </span>
                    {selectedLeadId === lead.id ? (
                      <ChevronUp className="h-4 w-4 text-cacao-muted" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-cacao-muted" />
                    )}
                  </div>
                </div>

                {/* Expanded View */}
                {selectedLeadId === lead.id && selectedLead && (
                  <div className="border-t border-cacao-dark-tertiary p-4 space-y-4">
                    {/* Subject */}
                    <div>
                      <label className="block text-xs font-medium text-cacao-muted mb-1">Subject</label>
                      {editingDraft ? (
                        <input
                          type="text"
                          value={draftSubject}
                          onChange={(e) => setDraftSubject(e.target.value)}
                          className="w-full rounded-lg border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-sm text-white placeholder-cacao-muted focus:border-cacao-gold focus:outline-none"
                          placeholder="Email subject..."
                        />
                      ) : (
                        <p className="text-sm text-white font-medium">{selectedLead.email_draft?.subject}</p>
                      )}
                    </div>

                    {/* Body */}
                    <div>
                      <label className="block text-xs font-medium text-cacao-muted mb-1">Message</label>
                      {editingDraft ? (
                        <textarea
                          value={draftBody}
                          onChange={(e) => setDraftBody(e.target.value)}
                          rows={6}
                          className="w-full rounded-lg border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-sm text-white placeholder-cacao-muted focus:border-cacao-gold focus:outline-none resize-none"
                          placeholder="Email body..."
                        />
                      ) : (
                        <p className="text-sm text-cacao-muted whitespace-pre-wrap">
                          {trimRepeatedTrailingSignature(
                            selectedLead.email_draft?.body || '',
                            signature
                          )}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {editingDraft ? (
                        <>
                          <button
                            onClick={handleSaveDraft}
                            className="flex items-center gap-2 rounded-lg bg-cacao-gold px-3 py-2 text-sm font-semibold text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                          >
                            <Save className="h-4 w-4" />
                            Save Draft
                          </button>
                          <button
                            onClick={() => {
                              setEditingDraft(false);
                              setDraftSubject(selectedLead.email_draft?.subject || '');
                              setDraftBody(selectedLead.email_draft?.body || '');
                            }}
                            className="flex items-center gap-2 rounded-lg border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:text-white transition-colors"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleSendLead(lead)}
                            disabled={sendingLead === lead.id}
                            className="flex items-center gap-2 rounded-lg bg-cacao-gold px-4 py-2 text-sm font-semibold text-cacao-dark hover:bg-cacao-gold-hover transition-colors disabled:opacity-50"
                          >
                            {sendingLead === lead.id ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-cacao-dark border-t-transparent" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                            Send Email
                          </button>
                          <button
                            onClick={() => setEditingDraft(true)}
                            className="flex items-center gap-2 rounded-lg border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-cacao-gold/30 hover:text-white transition-colors"
                          >
                            <Edit3 className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            onClick={handleRegenerate}
                            disabled={regenerating}
                            className="flex items-center gap-2 rounded-lg border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-cacao-gold/30 hover:text-white transition-colors disabled:opacity-50"
                          >
                            {regenerating ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-cacao-muted border-t-transparent" />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                            Rewrite
                          </button>
                          <button
                            onClick={() => handleSkipLead(lead.id)}
                            disabled={skippingLead === lead.id}
                            className="flex items-center gap-2 rounded-lg border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-red-500/30 hover:text-red-400 transition-colors disabled:opacity-50"
                          >
                            {skippingLead === lead.id ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-cacao-muted border-t-transparent" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                            Skip
                          </button>
                        </>
                      )}
                    </div>

                    {/* Apply Template */}
                    {!editingDraft && templates.length > 0 && (
                      <div className="border-t border-cacao-dark-tertiary pt-3">
                        <p className="text-xs font-medium text-cacao-muted mb-2">Apply Template</p>
                        <div className="flex flex-wrap gap-2">
                          {templates.map((template) => (
                            <button
                              key={template.id}
                              onClick={() => handleApplyTemplate(template, lead)}
                              className="rounded-lg border border-cacao-dark-tertiary px-2 py-1 text-xs text-cacao-muted hover:border-cacao-gold/30 hover:text-white transition-colors"
                            >
                              {template.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Right Column - Settings */}
        <div className="space-y-4">
          {/* Signature Section */}
          <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary">
            <button
              onClick={() => setSignatureExpanded(!signatureExpanded)}
              className="flex items-center justify-between w-full p-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10">
                  <PenLine className="h-4 w-4 text-blue-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-medium text-white">Email Signature</h3>
                  <p className="text-xs text-cacao-muted">
                    {signature ? 'Configured' : 'Not set'}
                  </p>
                </div>
              </div>
              {signatureExpanded ? (
                <ChevronUp className="h-4 w-4 text-cacao-muted" />
              ) : (
                <ChevronDown className="h-4 w-4 text-cacao-muted" />
              )}
            </button>

            {signatureExpanded && (
              <div className="border-t border-cacao-dark-tertiary p-4 space-y-3">
                <textarea
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-cacao-dark-tertiary bg-cacao-dark px-3 py-2 text-sm text-white placeholder-cacao-muted focus:border-cacao-gold focus:outline-none resize-none"
                  placeholder="Your email signature..."
                />
                <button
                  onClick={handleSaveSignature}
                  disabled={savingSignature}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-cacao-gold px-3 py-2 text-sm font-semibold text-cacao-dark hover:bg-cacao-gold-hover transition-colors disabled:opacity-50"
                >
                  {savingSignature ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-cacao-dark border-t-transparent" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Signature
                </button>
              </div>
            )}
          </div>

          {/* Templates Section */}
          <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary">
            <button
              onClick={() => setTemplatesExpanded(!templatesExpanded)}
              className="flex items-center justify-between w-full p-4"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-500/10">
                  <FileText className="h-4 w-4 text-purple-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-medium text-white">Message Templates</h3>
                  <p className="text-xs text-cacao-muted">{templates.length} template{templates.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              {templatesExpanded ? (
                <ChevronUp className="h-4 w-4 text-cacao-muted" />
              ) : (
                <ChevronDown className="h-4 w-4 text-cacao-muted" />
              )}
            </button>

            {templatesExpanded && (
              <div className="border-t border-cacao-dark-tertiary p-4 space-y-3">
                {/* Existing Templates */}
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark p-3"
                  >
                    {editingTemplate === template.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={template.name}
                          onChange={(e) => {
                            setTemplates((prev) =>
                              prev.map((t) => (t.id === template.id ? { ...t, name: e.target.value } : t))
                            );
                          }}
                          className="w-full rounded border border-cacao-dark-tertiary bg-cacao-dark-secondary px-2 py-1 text-sm text-white"
                          placeholder="Template name"
                        />
                        <input
                          type="text"
                          value={template.subject}
                          onChange={(e) => {
                            setTemplates((prev) =>
                              prev.map((t) => (t.id === template.id ? { ...t, subject: e.target.value } : t))
                            );
                          }}
                          className="w-full rounded border border-cacao-dark-tertiary bg-cacao-dark-secondary px-2 py-1 text-sm text-white"
                          placeholder="Subject line"
                        />
                        <textarea
                          value={template.body}
                          onChange={(e) => {
                            setTemplates((prev) =>
                              prev.map((t) => (t.id === template.id ? { ...t, body: e.target.value } : t))
                            );
                          }}
                          rows={4}
                          className="w-full rounded border border-cacao-dark-tertiary bg-cacao-dark-secondary px-2 py-1 text-sm text-white resize-none"
                          placeholder="Email body"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveTemplate(template)}
                            disabled={savingTemplate}
                            className="flex items-center gap-1 rounded bg-cacao-gold px-2 py-1 text-xs font-semibold text-cacao-dark"
                          >
                            <Save className="h-3 w-3" />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingTemplate(null)}
                            className="rounded px-2 py-1 text-xs text-cacao-muted hover:text-white"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-white">{template.name}</p>
                          <p className="text-xs text-cacao-muted truncate max-w-[180px]">{template.subject}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingTemplate(template.id)}
                            className="p-1 text-cacao-muted hover:text-white"
                          >
                            <Edit3 className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-1 text-cacao-muted hover:text-red-400"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* New Template Form */}
                {newTemplate ? (
                  <div className="rounded-lg border border-cacao-gold/30 bg-cacao-dark p-3 space-y-2">
                    <input
                      type="text"
                      value={newTemplate.name || ''}
                      onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                      className="w-full rounded border border-cacao-dark-tertiary bg-cacao-dark-secondary px-2 py-1 text-sm text-white"
                      placeholder="Template name"
                    />
                    <input
                      type="text"
                      value={newTemplate.subject || ''}
                      onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                      className="w-full rounded border border-cacao-dark-tertiary bg-cacao-dark-secondary px-2 py-1 text-sm text-white"
                      placeholder="Subject line"
                    />
                    <textarea
                      value={newTemplate.body || ''}
                      onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })}
                      rows={4}
                      className="w-full rounded border border-cacao-dark-tertiary bg-cacao-dark-secondary px-2 py-1 text-sm text-white resize-none"
                      placeholder="Email body (use {{first_name}}, {{company_name}}, etc.)"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddTemplate}
                        disabled={savingTemplate || !newTemplate.name || !newTemplate.body}
                        className="flex items-center gap-1 rounded bg-cacao-gold px-2 py-1 text-xs font-semibold text-cacao-dark disabled:opacity-50"
                      >
                        <Plus className="h-3 w-3" />
                        Create
                      </button>
                      <button
                        onClick={() => setNewTemplate(null)}
                        className="rounded px-2 py-1 text-xs text-cacao-muted hover:text-white"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setNewTemplate({ name: '', subject: '', body: '', tone: 'warm' })}
                    className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-cacao-dark-tertiary px-3 py-2 text-sm text-cacao-muted hover:border-cacao-gold/30 hover:text-white transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Template
                  </button>
                )}

                {/* Variable Reference */}
                <div className="text-xs text-cacao-muted pt-2 border-t border-cacao-dark-tertiary">
                  <p className="font-medium mb-1">Available variables:</p>
                  <p className="text-[10px] leading-relaxed">
                    <span className="text-cacao-gold">Lead:</span> {'{{first_name}}'}, {'{{last_name}}'}, {'{{company_name}}'}, {'{{title}}'}, {'{{signal_details}}'}
                  </p>
                  <p className="text-[10px] leading-relaxed mt-1">
                    <span className="text-cacao-gold">You:</span> {'{{my_name}}'}, {'{{my_title}}'}, {'{{my_city}}'}, {'{{my_phone}}'},{' '}
                    {'{{city_phrase}}'}, {'{{phone_phrase}}'} (optional; empty when city/phone missing)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="rounded-xl border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4">
            <h3 className="font-medium text-white mb-3">Today's Progress</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-cacao-muted">Ready to send</span>
                <span className="font-semibold text-white">{readyToSend.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-cacao-muted">Pending leads</span>
                <span className="font-semibold text-white">
                  {leads.filter((l) => l.status === 'pending').length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-cacao-muted">Sent today</span>
                <span className="font-semibold text-white">
                  {leads.filter((l) => {
                    if (!l.contacted_at) return false;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return new Date(l.contacted_at) >= today;
                  }).length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
